
import Component from "vue-class-component";
import Vue from "vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import {
    AddPilotagePracticeRequest,
    PilotagePracticeActions,
    PracticeKind,
    AreasClient,
    AreaOption,
    PracticePeriod,
    ResultError,
    HarboursClient,
    HarbourOption,
    PilotageType,
    ShipsClient,
    ShipInfoModel,
    ShipTypesClient,
    ShipTypeLocalizedModel,
    CountryLightModel,
    DocumentAttributesClient,
    OrganizationOption,
    PracticesClient
} from "@/api-client";
import PracticePeriods from "@/features/documents/practices/components/PracticePeriods.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {Prop} from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import ShipInformation from "@/features/documents/practices/components/ShipInformation.vue";
import moment from "moment";
import userInfo from "@/features/shared/components/UserInfo";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import DropdownSelector from "@/components/DropdownSelector.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import ShipInfoModal from "@/features/documents/practices/components/ShipInfoModal.vue";
import { debounceFn } from "debounce-decorator-ts";

@Component({
    components: {
        CheckboxInput,
        DropdownSelector,
        HeadingGroup,
        LabelOptional,
        PracticePeriods,
        ButtonWithConfirm,
        ShipInfoModal,
        ShipInformation
    }
})
export default class PilotagePracticeUpsertForm extends Vue {
    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    model!: AddPilotagePracticeRequest;

    @Prop({required: true})
    actions!: PilotagePracticeActions;

    @Prop({required: true})
    submitCallback!: (model: AddPilotagePracticeRequest, saveAndAddNew?: boolean) => Promise<void>;

    @Prop({default: false})
    isEditMode!: boolean;

    validationErrors = ResultError.createEmpty();

    warningMatching = false;
    
    // Store harbour for reuse when "Save and Add New" is clicked
    savedHarbourId: string | null | undefined = null;

    areaOptions: AreaOption[] = [];
    harbourOptions: HarbourOption[] = [];
    
    pilotageTypeOptions = [
        {text: this.$ls.Documents_Practices_PilotageType_Pilotage, value: PilotageType.Pilotage},
        {text: this.$ls.Documents_Practices_PilotageType_PracticalPilotage, value: PilotageType.PracticalPilotage},
    ];

    // Ship selection
    shipsFromSearch: ShipInfoModel[] = [];
    selectedShip: ShipInfoModel | null = null;
    searching = false;
    shipTypeOptions: ShipTypeLocalizedModel[] = [];
    organizationOptions: OrganizationOption[] = [];
    countries: CountryLightModel[] = [];

    submitLoader = new Loader();
    isCreated = false;
    isSeafarer = userInfo.seafarerId === this.seafarerId;

    async created() {
        await Promise.all(
            [
                new AreasClient().areaOptions().then(x => this.areaOptions = x.areas),
                new HarboursClient().harbourOptions().then(x => {
                    // Add empty option at the beginning for optional harbour selection
                    const emptyOption = new HarbourOption();
                    emptyOption.value = '';
                    emptyOption.text = '-';
                    this.harbourOptions = [
                        emptyOption,
                        ...x.harbourOptions
                    ];
                }),
                new DocumentAttributesClient().countries().then(x => this.countries = x),
                new ShipTypesClient().shipTypesLocalized("").then(x => this.shipTypeOptions = x.shipTypesLocalized),
                new PracticesClient().inlandVesselOrganizationOptions().then(x => this.organizationOptions = x.organizationOptions)
            ]
        );

        // Set default practice kind to Work for pilotage practice
        this.model.kind = PracticeKind.Work;

        if (!this.model.practicePeriods) {
            this.model.practicePeriods = [];
        }

        // Load existing ship if ship object is set
        if (this.model.ship) {
            this.selectedShip = this.model.ship;
        }

        this.$watch(() => this.model, () => {
            this.validationErrors.clearKey("practicePeriods");
            if(this.model.issuedOn && moment().startOf("day").diff(this.model.issuedOn) < 0){
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issuedOn");
            }
            else {
                this.validationErrors.clearKey("issuedOn");
                this.validationErrors.clear();
                if (this.model.issuedOn && this.model.practicePeriods && this.model.practicePeriods.length > 0) {
                    this.model.practicePeriods.forEach(period => {
                        if (moment(period.startedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                        if (period.endedOn && moment(period.endedOn).startOf("day").diff(this.model.issuedOn) > 0) {
                            if(this.validationErrors.hasMessages("practicePeriods")){
                                this.validationErrors.clearKey("practicePeriods");
                            }
                            this.validationErrors.appendMessage(this.$ls.Practice_Validation_PeriodsAfterIssuedOn, "practicePeriods");
                        }
                    });
                }
            }
        },{deep:true});

        this.isCreated = true;
    }

    get canSubmit() {
        return this.validationErrors.isEmpty && !this.submitLoader.loading;
    }

    private addPracticePeriod(period: PracticePeriod) {
        this.model.practicePeriods.push(period);
    }

    private removePracticePeriod(index: number) {
        this.model.practicePeriods.splice(index, 1);
    }

    // Ship selection methods
    async searchShips(searchTerm: string) {
        this.searching = true;
        await this.searchShipsDebounced(searchTerm);
    }

    @debounceFn(500, "searchShipsFn")
    async searchShipsDebounced(searchTerm: string) {
        if (searchTerm.trim() !== '') {
            this.shipsFromSearch = await new ShipsClient().search(searchTerm);
            this.searching = false;
        } else {
            this.shipsFromSearch = [];
            this.searching = false;
        }
    }

    selectShip(ship: ShipInfoModel) {
        this.selectedShip = ship;
        // Store the entire ship object instead of just the ID
        this.model.ship = ship;
        this.model.shipVisitNumber = ship.imoNr || null; // Store IMO in shipVisitNumber
    }

    clearShipSelection() {
        this.selectedShip = null;
        this.model.ship = null;
        this.model.shipVisitNumber = null;
    }
    
    getShipDescription(ship: ShipInfoModel) {
        return `${ship.name} ${ship.imoNr || ''}`;
    }
    
    openAddShipModal() {
        this.$bvModal.show('modal-ship-add');
    }
    
    onShipSaved(newShip: ShipInfoModel) {
        // After saving a new ship, select it
        this.selectedShip = newShip;
        // Store the entire ship object
        this.model.ship = newShip;
        this.model.shipVisitNumber = newShip.imoNr || null; // Store IMO in shipVisitNumber
        this.shipsFromSearch = [newShip, ...this.shipsFromSearch];
    }
    
    async loadShipById(shipId: string) {
        try {
            // Search for ship by ID to get full details
            const ships = await new ShipsClient().search(shipId);
            if (ships && ships.length > 0) {
                this.selectedShip = ships[0];
            }
        } catch (error) {
            // Failed to load ship details
        }
    }

    private async submit() {
        
        if (!this.canSubmit) {
            return;
        }

        await this.submitLoader.startLoading();
        this.validationErrors.clear();
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch((error: ResultError) => {
                this.validationErrors = error;
                toasts.failure(this.$ls.Global_Toast_FormSaveFailure);
            })
        this.submitLoader.endLoading();
    }
    
    private async submitAndAddNew() {
       
        if (!this.canSubmit) {
            return;
        }

        await this.submitLoader.startLoading();
        this.validationErrors.clear();
        
        // Save current harbour for reuse
        this.savedHarbourId = this.model.harbourId;
        
        await this.submitCallback(this.model, true)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
                // Reset form but keep harbour
                this.resetFormForNew();
                   window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            })
            .catch((error: ResultError) => {
                this.validationErrors = error;
                toasts.failure(this.$ls.Global_Toast_FormSaveFailure);
            })
        this.submitLoader.endLoading();
     
    }
    
    private resetFormForNew() {
        // Save data that should be cloned
        // const savedAreaId = this.model.areaId;
        const savedHarbourId = this.model.harbourId;
        this.model.kind = PracticeKind.Work;
        // const savedPilotageType = this.model.pilotageType;
        // const savedIssuedOn = this.model.issuedOn;
        // const savedShip = this.model.ship;
        // const savedPracticePeriods = this.model.practicePeriods ? [...this.model.practicePeriods] : [];

        // Create new model instance
        this.model.areaId = null;
        this.model.harbourId = null;
        this.model.pilotageType = null;
        this.model.ship = null;
        this.model.shipVisitNumber = null;
        this.model.issuedOn = null;
        this.model.addedFiles = [];
        this.model.additionalNotes = null;
        this.model.comment = null;

        this.model.practicePeriods = [];
        this.model.existingFiles = [];

        // Restore saved data for convenience
        // if (savedAreaId) {
        //     this.model.areaId = savedAreaId;
        // }
        if (savedHarbourId) {
            this.model.harbourId = savedHarbourId;
        }
        // if (savedPilotageType !== undefined) {
        //     this.model.pilotageType = savedPilotageType;
        // }
        // if (savedIssuedOn) {
        //     this.model.issuedOn = savedIssuedOn;
        // }
        // if (savedShip) {
        //     this.selectedShip = savedShip;
        //     this.model.ship = savedShip;
        //     this.model.shipVisitNumber = savedShip.imoNr || null;
        // } else {
        //     this.selectedShip = null;
        //     this.model.ship = null;
        //     this.model.shipVisitNumber = null;
        // }

        // Clone practice periods
        // if (savedPracticePeriods.length > 0) {
        //     this.model.practicePeriods = savedPracticePeriods.map(period => {
        //         const newPeriod = new PracticePeriod();
        //         newPeriod.startedOn = period.startedOn;
        //         newPeriod.endedOn = period.endedOn;
        //         newPeriod.durationInDays = period.durationInDays;
        //         newPeriod.navigationalWatchDays = period.navigationalWatchDays;
        //         newPeriod.engineRoomWatchDays = period.engineRoomWatchDays;
        //         newPeriod.voyageCount = period.voyageCount;
        //         newPeriod.notes = period.notes;
        //         newPeriod.inOutFlag = period.inOutFlag;
        //         newPeriod.instructorId = period.instructorId;
        //         return newPeriod;
        //     });
        // }

        // Clear validation errors
        this.validationErrors.clear();
    }
}
